/** Typed fetch wrapper. Adds CSRF + workspace headers, parses the API's {error:{code,message,details}} shape. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details: Record<string, any> = {}) {
    super(message);
  }
  get fields(): Record<string, string> {
    return (this.details?.fields as Record<string, string>) ?? {};
  }
}

let workspaceId: string | null = null;
export const setWorkspaceId = (id: string | null) => { workspaceId = id; };
export const getWorkspaceId = () => workspaceId;

function cookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

let onUnauthenticated: ((code: string) => void) | null = null;
export const setUnauthenticatedHandler = (fn: (code: string) => void) => { onUnauthenticated = fn; };

type Opts = { method?: string; body?: unknown; query?: Record<string, any>; signal?: AbortSignal; workspace?: string | null; raw?: boolean };

export async function api<T = any>(path: string, opts: Opts = {}): Promise<T> {
  const method = (opts.method ?? (opts.body !== undefined ? 'POST' : 'GET')).toUpperCase();
  const headers: Record<string, string> = { Accept: 'application/json' };
  const ws = opts.workspace === undefined ? workspaceId : opts.workspace;
  if (ws) headers['X-Workspace-Id'] = ws;
  if (method !== 'GET' && method !== 'HEAD') {
    let csrf = cookie('bf_csrf');
    if (!csrf) { await fetch('/api/v1/auth/csrf', { credentials: 'include' }); csrf = cookie('bf_csrf'); }
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) { headers['Content-Type'] = 'application/json'; body = JSON.stringify(opts.body); }
  let url = path.startsWith('/') ? path : `/api/v1/${path}`;
  if (opts.query) {
    const q = new URLSearchParams();
    Object.entries(opts.query).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, String(v)); });
    const s = q.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, credentials: 'include', signal: opts.signal });
  } catch (e: any) {
    if (e?.name === 'AbortError') throw e;
    throw new ApiError(0, 'network_error', 'Can’t reach the server. Check your connection and try again.');
  }
  if (opts.raw) return res as unknown as T;
  const text = await res.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) {
    const err = data?.error ?? { code: 'http_error', message: res.statusText };
    if (res.status === 401 && onUnauthenticated) onUnauthenticated(err.code);
    throw new ApiError(res.status, err.code, err.message ?? 'Request failed', err.details ?? {});
  }
  return data as T;
}

export const get = <T = any>(p: string, query?: Record<string, any>) => api<T>(p, { query });
export const post = <T = any>(p: string, body?: unknown) => api<T>(p, { method: 'POST', body: body ?? {} });
export const put = <T = any>(p: string, body?: unknown) => api<T>(p, { method: 'PUT', body: body ?? {} });
export const patch = <T = any>(p: string, body?: unknown) => api<T>(p, { method: 'PATCH', body: body ?? {} });
export const del = <T = any>(p: string) => api<T>(p, { method: 'DELETE' });
